import type { DataSchema, FormItemType, Schema } from '../../types';
import type { AnySchema } from 'yup';
import { mixed, object } from 'yup';
import { getRecordItemValidation } from './recordItemsValidation.ts';
import { capitalize } from 'lodash';
import { VISIBILITY_STATE } from '../../types/enums';
import { isTableCellEditable } from '../table';
import { validateHeliosNumberRange } from '../helios/fieldValidation';

const DEFAULT_SKIP_VALIDATION_CELLS = ['FileUpload', 'Comment', 'Button'] satisfies FormItemType[];

export type TableCellValidationOptions = {
  skipCells?: FormItemType[];
  visibilityResults?: Record<string, { visibilityState?: string } | undefined>;
  isHeliosTable?: boolean;
  isEditableColumnsAllowed?: boolean;
  editRowIds?: readonly string[] | null;
};

export const tableCellValidation = <T extends keyof P, P extends DataSchema>(
  dataSchema: P,
  rows: Schema<T, P>[],
  options: TableCellValidationOptions = {}
) => {
  const validationShape: Record<string, AnySchema> = {};
  const skipCells = options.skipCells ?? DEFAULT_SKIP_VALIDATION_CELLS;
  const { visibilityResults, isHeliosTable = false, editRowIds } = options;

  const isEditableColumnsAllowed = options.isEditableColumnsAllowed ?? true;

  rows.forEach((row) => {
    const rowId = row.identifier;
    const rowValidation: Record<string, AnySchema> = {};

    if (rowId == null) return;

    Object.keys(dataSchema).forEach((key) => {
      const cellSchema = dataSchema[key];

      if (!('formItemDefinition' in cellSchema) && 'type' in cellSchema && cellSchema.type !== 'id') {
        cellSchema.formItemDefinition = {
          type: capitalize(cellSchema.type) as FormItemType,
        };
      }

      if (cellSchema.formItemDefinition) {
        const isColumnEditable = Boolean(cellSchema.isEditable);
        const isHidden = visibilityResults?.[`${rowId}.${key}`]?.visibilityState === VISIBILITY_STATE.HIDDEN;
        const shouldSkipValidation = skipCells.includes(cellSchema.formItemDefinition.type);

        const shouldValidate = isTableCellEditable(rowId, isColumnEditable, {
          isHeliosTable,
          isEditableColumnsAllowed,
          isRowInEditMode: Boolean(editRowIds?.includes(rowId)),
        });

        if (shouldSkipValidation || isHidden || !shouldValidate) {
          rowValidation[key] = mixed();
        } else {
          const validation = (getRecordItemValidation(cellSchema.formItemDefinition) ?? mixed()) as AnySchema;
          rowValidation[key] = validation.test('helios-number-range', '', function (value) {
            const result = validateHeliosNumberRange(
              value,
              cellSchema.dataType ?? '',
              cellSchema.formatting?.formatValue?.typeSpecific?.formatNumber
            );
            return result === true || this.createError({ message: result });
          });
        }
      }
    });

    validationShape[rowId] = object().shape(rowValidation);
  });

  return object().shape(validationShape);
};
